import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useTooltips } from '../contexts/HelpTooltipContext';

/**
 * Inline help tooltip. Renders an ⓘ icon that shows a popover on click.
 * Looks up content by `id` from the HelpTooltipContext (loaded from /content/tooltips.json).
 * Renders nothing if the id isn't found — safe to add anywhere, even before content is published.
 *
 * Usage:
 *   <HelpTooltip id="bookmark-tags" />
 *   <HelpTooltip id="collection-share" className="ml-1" />
 */
// Minimum px of space required above the icon before flipping to open downward
const FLIP_THRESHOLD = 160;

const HelpTooltip = ({ id, className = '' }) => {
  const tooltips = useTooltips();
  const tooltip = tooltips[id];
  const [open, setOpen] = useState(false);
  const [opensBelow, setOpensBelow] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  if (!tooltip) return null;

  const handleToggle = () => {
    if (!open && btnRef.current) {
      const { top } = btnRef.current.getBoundingClientRect();
      setOpensBelow(top < FLIP_THRESHOLD);
    }
    setOpen(o => !o);
  };

  // Popover and arrow classes change based on direction
  const popoverPos = opensBelow
    ? 'top-full mt-2'
    : 'bottom-full mb-2';

  // Arrow: points up when popover is below, points down when popover is above
  const arrowBorder = opensBelow
    ? 'border-b-gray-200 dark:border-b-gray-600 border-t-transparent'
    : 'border-t-gray-200 dark:border-t-gray-600 border-b-transparent';
  const arrowFill = opensBelow
    ? 'border-b-white dark:border-b-gray-800 border-t-transparent'
    : 'border-t-white dark:border-t-gray-800 border-b-transparent';
  const arrowPos = opensBelow ? 'bottom-full' : 'top-full';
  const arrowFillOffset = opensBelow ? 'bottom-[calc(100%-1px)]' : 'top-[calc(100%-1px)]';

  return (
    <span ref={ref} className={`relative inline-flex items-center ${className}`}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        className="text-gray-400 hover:text-primary-600 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-full"
        aria-label="Help"
        aria-expanded={open}
      >
        <InformationCircleIcon className="h-4 w-4" />
      </button>

      {open && (
        <div
          role="tooltip"
          className={`absolute z-50 left-1/2 -translate-x-1/2 ${popoverPos}
            w-64 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 shadow-lg p-3 text-left`}
        >
          {/* Arrow */}
          <span className={`absolute ${arrowPos} left-1/2 -translate-x-1/2 block w-0 h-0
            border-l-[6px] border-l-transparent
            border-r-[6px] border-r-transparent
            border-t-[6px] border-b-[6px] ${arrowBorder}`} />
          <span className={`absolute ${arrowFillOffset} left-1/2 -translate-x-1/2 block w-0 h-0
            border-l-[6px] border-l-transparent
            border-r-[6px] border-r-transparent
            border-t-[6px] border-b-[6px] ${arrowFill}`} />

          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{tooltip.content}</p>

          {tooltip.articleId && (
            <Link
              to={`/help/${tooltip.articleId}`}
              onClick={() => setOpen(false)}
              className="mt-2 inline-block text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
            >
              Learn more →
            </Link>
          )}
        </div>
      )}
    </span>
  );
};

export default HelpTooltip;
